import React, { useState } from 'react';
import styled from 'styled-components';

/* El panel es una nota al margen, no otra tabla: sin caja exterior ni bordes
   contiguos. Las secciones se separan por gap y cada una se sostiene sola. */
const Wrapper = styled.div`
  flex-shrink: 0;
`;

/* Modo autónomo: el toggle es su propia caja y el cuerpo va debajo, separado. */
const Toggle = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 14px;
  margin-bottom: ${({ $open }) => ($open ? '8px' : '0')};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  cursor: pointer;
  color: ${({ theme }) => theme.colors.txtPrincipal};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-align: left;
  transition-property: background-color, border-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
    border-color: ${({ theme }) => theme.colors.borderDefault};
  }

  > i:first-child { color: ${({ theme }) => theme.colors.primary}; }

  .chevron {
    margin-left: auto;
    font-size: 11px;
    color: ${({ theme }) => theme.colors.txtMuted};
    transition-property: transform;
    transition-duration: 0.2s;
    transition-timing-function: ease-in-out;
    transform: ${({ $open }) => $open ? 'rotate(180deg)' : 'rotate(0deg)'};
  }
`;

const Body = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 8px;
`;

/* Color como acento (barra superior + ícono), no como fondo: los tres bloques
   saturados competían con los datos operacionales de la pantalla. */
const accentOf = ($variant, theme) => {
  if ($variant === 'prereq') return theme.colors.primary;
  if ($variant === 'tips') return theme.colors.warning;
  if ($variant === 'next') return theme.colors.success;
  return theme.colors.borderDefault;
};

const Section = styled.div`
  position: relative;
  padding: 11px 14px 12px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${({ $variant, theme }) => accentOf($variant, theme)};
    opacity: 0.55;
  }
`;

/* Encabezado como etiqueta, no como celda — mismo criterio que TasksTable. */
const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 9px;
  color: ${({ theme }) => theme.colors.txtMuted};

  i {
    font-size: 12px;
    color: ${({ $variant, theme }) => accentOf($variant, theme)};
  }
`;

const ItemList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const Item = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 7px;
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.txtSecondary};

  i {
    margin-top: 3px;
    flex-shrink: 0;
    font-size: 10px;
    color: ${({ $variant, theme }) => accentOf($variant, theme)};
    opacity: 0.7;
  }

  /* bi-dot renderiza mucho más chico que el resto del set: compensación óptica. */
  i.bi-dot {
    font-size: 16px;
    margin-top: -1px;
    margin-left: -3px;
    margin-right: -3px;
  }
`;

export const HelpToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 4px;
  border: 1px solid ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.borderDefault};
  background: ${({ $active, theme }) => $active ? theme.colors.primaryAlpha : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.txtSecondary};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition-property: background-color, color, border-color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryAlpha};
  }
`;

const SECTION_ICONS = { prereq: 'bi bi-check2-circle', tips: 'bi bi-lightbulb', next: 'bi bi-arrow-right-circle' };
const ITEM_ICONS    = { prereq: 'bi bi-check2',        tips: 'bi bi-dot',        next: 'bi bi-arrow-right' };
const SECTION_LABELS = { prereq: 'Antes de empezar', tips: 'Recomendaciones', next: 'Próximos pasos' };

const PanelBody = ({ sections }) => (
  <Body>
    {sections.map(({ key, items }) => (
      <Section key={key} $variant={key}>
        <SectionTitle $variant={key}>
          <i className={SECTION_ICONS[key]} />
          {SECTION_LABELS[key]}
        </SectionTitle>
        <ItemList>
          {items.map((item, i) => (
            <Item key={i} $variant={key}>
              <i className={ITEM_ICONS[key]} />
              {item}
            </Item>
          ))}
        </ItemList>
      </Section>
    ))}
  </Body>
);

/**
 * HelpPanel — panel contextual de ayuda.
 *
 * Modo autónomo (por defecto): muestra un botón toggle propio.
 * Modo inline (inlineOpen=true): sin botón propio, siempre visible.
 *   Usar cuando el botón de toggle está en el componente padre (ej. Header).
 */
export default function HelpPanel({
  prereqs,
  tips,
  nextSteps,
  defaultOpen = false,
  title = 'Ayuda y recomendaciones',
  inlineOpen = false,
}) {
  const [open, setOpen] = useState(defaultOpen);

  const sections = [
    prereqs?.length   && { key: 'prereq', items: prereqs },
    tips?.length      && { key: 'tips',   items: tips },
    nextSteps?.length && { key: 'next',   items: nextSteps },
  ].filter(Boolean);

  if (!sections.length) return null;

  if (inlineOpen) {
    return (
      <Wrapper>
        <PanelBody sections={sections} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Toggle $open={open} onClick={() => setOpen(v => !v)} type="button">
        <i className="bi bi-question-circle" />
        {title}
        <i className="bi bi-chevron-down chevron" />
      </Toggle>
      {open && <PanelBody sections={sections} />}
    </Wrapper>
  );
}
